import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, ChevronDown, Check, Info, AlertCircle } from 'lucide-react';
import { useState, useRef, useEffect, memo } from 'react';
import useScrollLock from '../../hooks/useScrollLock';
import { formatCurrency } from '../../utils/format';
import InlineMDPreview from './InlineMDPreview';

/**
 * Modern StatsCard component with isolated data fetching
 * Features:
 * - Self-contained time filter state (no parent coupling)
 * - Independent React Query data fetching with unique query key
 * - Only this card re-renders when its filter changes
 * - Automatic caching via React Query
 * - Loading and error states
 * - Interactive popup with detailed stats
 */
const StatsCard = memo(({
  title,
  icon: Icon,
  color = 'blue',
  delay = 0,
  metricKey, // 'books' | 'users' | 'orders' | 'sales' - determines which data to extract
  showTimeSelector = true,
  detailsDataBuilder = null, // Function to build details data: (stats, recentOrders) => detailsData
  mockData = null, // Optional mock data passed from parent
}) => {
  // Local state - completely isolated from parent and siblings
  const [timeRange, setTimeRange] = useState('Ce mois-ci');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [activeView, setActiveView] = useState('net');
  const dropdownRef = useRef(null);
  const cardRef = useRef(null);
  const popupRef = useRef(null);

  // Lock background scroll when popup is visible
  useScrollLock(isPopupVisible);

  const timeRangeOptions = ['Aujourd\'hui', 'Cette semaine', 'Ce mois-ci'];

  // Use mock data if provided, otherwise null (will fetch from API in real implementation)
  const data = mockData;
  const isLoading = false;
  const isError = false;
  const error = null;

  // Extract metric-specific data
  const getMetricData = () => {
    if (!data) return { value: 0, growth: null };

    const getValueForTimeRange = (metricObj) => {
      if (!metricObj) return 0;
      switch (timeRange) {
        case "Aujourd'hui": return metricObj.today ?? 0;
        case 'Cette semaine': return metricObj.thisWeek ?? 0;
        case 'Ce mois-ci': return metricObj.thisMonth ?? 0;
        default: return metricObj.thisMonth ?? 0;
      }
    };

    const metricMap = {
      bestSellingBook: {
        value: data.bestSellingBook?.title || 'N/A',
        growth: data.growth?.bestSellingBook,
      },
      newUsers: {
        value: getValueForTimeRange(data.newUsers),
        growth: data.growth?.newUsers,
      },
      orders: {
        value: getValueForTimeRange(data.orders),
        growth: data.growth?.orders,
      },
      sales: {
        value: formatCurrency(getValueForTimeRange(data.sales)),
        grossValue: data.grossSales ? formatCurrency(getValueForTimeRange(data.grossSales)) : null,
        growth: data.growth?.sales,
      },
    };

    return metricMap[metricKey] || { value: 0, growth: null };
  };

  const { value, grossValue, growth } = getMetricData();
  const detailsData = detailsDataBuilder && data ? detailsDataBuilder(data, timeRange) : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close popup when clicking outside the card and scroll to top
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPopupVisible && cardRef.current && !cardRef.current.contains(event.target)) {
        setIsPopupVisible(false);
        // Smooth scroll to top when closing popup
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPopupVisible]);

  // Reset active section tab when popup closes
  useEffect(() => {
    if (!isPopupVisible) setActiveSection(0);
  }, [isPopupVisible]);

  // Auto-scroll to make popup fully visible when opened
  useEffect(() => {
    if (isPopupVisible && popupRef.current) {
      // Small delay to ensure popup is fully rendered
      setTimeout(() => {
        const popupRect = popupRef.current.getBoundingClientRect();
        const popupBottom = popupRect.bottom;
        const viewportHeight = window.innerHeight;

        if (popupBottom > viewportHeight - 40) {
          const scrollAmount = popupBottom - viewportHeight + 60; // Extra padding
          window.scrollBy({
            top: scrollAmount,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [isPopupVisible]);

  // Handle card click for popup - single click to toggle
  const handleCardClick = (e) => {
    // Don't trigger if clicking on dropdown
    if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
      return;
    }

    if (detailsData) {
      setIsPopupVisible(!isPopupVisible);
    }
  };

  // Handle time range change - purely local, no parent notification
  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
    setIsDropdownOpen(false);
    // Query will automatically refetch with new timeRange
  };
  // Modern color schemes with gradients and enhanced contrast
  const colorClasses = {
    blue: {
      bg: 'from-blue-500 to-blue-600',
      icon: 'bg-gradient-to-br from-blue-400 to-blue-600',
      shadow: 'hover:shadow-blue-200',
      ring: 'hover:ring-blue-100',
      badge: 'bg-blue-100 text-blue-700',
      tabActive: 'bg-white shadow-sm text-blue-700',
    },
    green: {
      bg: 'from-emerald-500 to-emerald-600',
      icon: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
      shadow: 'hover:shadow-emerald-200',
      ring: 'hover:ring-emerald-100',
      badge: 'bg-emerald-100 text-emerald-700',
      tabActive: 'bg-white shadow-sm text-emerald-700',
    },
    purple: {
      bg: 'from-purple-500 to-purple-600',
      icon: 'bg-gradient-to-br from-purple-400 to-purple-600',
      shadow: 'hover:shadow-purple-200',
      ring: 'hover:ring-purple-100',
      badge: 'bg-purple-100 text-purple-700',
      tabActive: 'bg-white shadow-sm text-purple-700',
    },
    red: {
      bg: 'from-rose-500 to-rose-600',
      icon: 'bg-gradient-to-br from-rose-400 to-rose-600',
      shadow: 'hover:shadow-rose-200',
      ring: 'hover:ring-rose-100',
      badge: 'bg-rose-100 text-rose-700',
      tabActive: 'bg-white shadow-sm text-rose-700',
    },
  };

  const colors = colorClasses[color];

  // Loading state
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.4 }}
        className="relative bg-white rounded-2xl border border-gray-100 p-6"
      >
        <div className="space-y-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-10 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        </div>
      </motion.div>
    );
  }

  // Error state
  if (isError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay, duration: 0.4 }}
        className="relative bg-white rounded-2xl border border-red-200 p-6"
      >
        <div className="flex flex-col items-center justify-center space-y-2 text-center">
          <AlertCircle className="w-8 h-8 text-red-500" />
          <p className="text-sm text-red-600 font-medium">Erreur de chargement</p>
          <p className="text-xs text-gray-500">{error?.message || 'Une erreur est survenue'}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay,
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1.0] // Custom easing for smoother animation
      }}
      whileHover={{
        y: -4,
        transition: { duration: 0.2 }
      }}
      onClick={handleCardClick}
      className={`
        relative
        bg-white rounded-2xl
        border border-gray-100
        p-6
        hover:shadow-xl ${colors.shadow}
        hover:ring-2 ${colors.ring}
        transition-all duration-300
        cursor-pointer
        group
        ${isPopupVisible ? 'z-[100]' : 'z-0'}
      `}
      style={{ overflow: 'visible' }}
    >
      {/* Subtle gradient overlay for depth */}
      <div className="absolute top-0 right-0 w-32 h-32 opacity-5 pointer-events-none overflow-hidden rounded-2xl">
        <div className={`w-full h-full bg-gradient-to-br ${colors.bg} rounded-full blur-2xl`} />
      </div>

      {/* Card Content - Structured Layout */}
      <div className="relative space-y-4">
        {/* Header Row: Title and Controls */}
        <div className="flex items-start justify-between gap-3">
          {/* Title */}
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex-1 min-w-0">
            {title}
          </p>

          {/* Right controls: Net/Brut toggle + Time Selector */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Net/Brut cycling toggle — only for cards with gross data */}
            {grossValue && (
              <button
                onClick={(e) => { e.stopPropagation(); setActiveView(v => v === 'net' ? 'brut' : 'net'); }}
                className="
                  text-[10px] font-bold uppercase tracking-wider
                  px-2 py-1.5 rounded-lg border
                  transition-all duration-200
                  bg-gray-200 text-gray-800 border-gray-300
                "
              >
                {activeView === 'net' ? 'Net' : 'Brut'}
              </button>
            )}

            {/* Custom Time Range Selector */}
            {showTimeSelector && (
              <div ref={dropdownRef} className="relative z-20">
                {/* Dropdown Trigger Button */}
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="
                    flex items-center gap-1.5
                    bg-gray-50 hover:bg-gray-100
                    text-gray-700
                    text-xs font-medium
                    px-3 py-1.5
                    rounded-lg
                    border border-gray-200
                    cursor-pointer
                    transition-all duration-200
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                    whitespace-nowrap
                  "
                >
                  <span className="text-left">{timeRange}</span>
                  <motion.div
                    animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                  </motion.div>
                </button>

                {/* Custom Dropdown Menu */}
                <AnimatePresence>
                  {isDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="
                        absolute top-full right-0 mt-2
                        min-w-full
                        bg-white
                        border border-gray-200
                        rounded-lg
                        shadow-lg
                        overflow-hidden
                        z-50
                      "
                    >
                      {timeRangeOptions.map((option) => (
                        <button
                          key={option}
                          onClick={() => handleTimeRangeChange(option)}
                          className={`
                            w-full
                            flex items-center justify-between
                            px-3 py-2
                            text-xs font-medium
                            text-left
                            transition-colors duration-150
                            whitespace-nowrap
                            ${timeRange === option
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-gray-700 hover:bg-gray-50'
                            }
                          `}
                        >
                          <span>{option}</span>
                          {timeRange === option && (
                            <Check className="w-3.5 h-3.5 text-blue-600 flex-shrink-0 ml-2" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Row: Value and Icon */}
        <div className="flex items-center justify-between gap-3">
          {/* Value */}
          <div className="flex-1 min-w-0">
            <AnimatePresence mode="wait">
              <motion.p
                key={grossValue ? activeView : 'static'}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="text-3xl font-bold text-gray-900 tracking-tight line-clamp-1"
              >
                <InlineMDPreview>{grossValue ? (activeView === 'net' ? value : grossValue) : value}</InlineMDPreview>
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Icon */}
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.2 }}
            className={`
              flex-shrink-0
              w-14 h-14
              flex items-center justify-center
              rounded-xl
              ${colors.icon}
              shadow-lg
              group-hover:shadow-xl
              transition-shadow duration-300
            `}
          >
            <Icon className="w-6 h-6 text-white" strokeWidth={2} />
          </motion.div>
        </div>

        {/* Growth Indicator Row */}
        {growth && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`
              flex items-center gap-1 px-2 py-0.5 rounded-full
              ${growth.isPositive
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-rose-50 text-rose-700'
              }
            `}>
              {growth.isPositive ? (
                <TrendingUp className="w-3 h-3" />
              ) : (
                <TrendingDown className="w-3 h-3" />
              )}
              <span className="text-[11px] font-bold">
                {growth.isPositive ? '+' : ''}{growth.value}%
              </span>
            </div>
            <span className="text-[11px] text-gray-500">
              {timeRange === 'Aujourd\'hui' ? 'depuis hier' :
               timeRange === 'Cette semaine' ? 'depuis la semaine dernière' :
               'depuis le mois dernier'}
            </span>
          </div>
        )}
      </div>


      {/* Interactive Popup with Details */}
      <AnimatePresence>
        {isPopupVisible && detailsData && (
          <motion.div
              ref={popupRef}
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1] // Smooth cubic-bezier easing
              }}
              className="absolute left-0 right-0 top-full mt-4 z-[200] px-2 sm:px-0"
              style={{ pointerEvents: 'none' }}
            >
              <div
                className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-w-sm mx-auto sm:mx-0 relative ring-4 ring-black/5"
                style={{ pointerEvents: 'auto' }}
              >
              {/* Popup Header */}
              <div className={`bg-gradient-to-r ${colors.bg} px-4 py-3`}>
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-white" />
                  <h3 className="text-white font-semibold text-sm">Détails de {title}</h3>
                </div>
              </div>

              {/* Popup Content */}
              <div className="p-4 space-y-4">
                {/* Description */}
                {detailsData.description && (
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {detailsData.description}
                  </p>
                )}

                {/* Sectioned breakdown with tab navigation (e.g. Net / Brut) */}
                {detailsData.sections ? (
                  <div className="space-y-3">
                    {/* Tab bar */}
                    <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded-md">
                      {detailsData.sections.map((section, idx) => (
                        <button
                          key={section.key}
                          onClick={(e) => { e.stopPropagation(); setActiveSection(idx); }}
                          className={`
                            flex-1 py-1 px-1 rounded text-[11px] font-semibold
                            transition-all duration-200
                            ${activeSection === idx
                              ? colors.tabActive
                              : 'text-gray-500 hover:text-gray-700'
                            }
                          `}
                        >
                          {section.label}
                        </button>
                      ))}
                    </div>

                    {/* Animated section content */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeSection}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.18 }}
                      >
                        {detailsData.sections[activeSection].breakdown.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                          >
                            <span className="text-sm text-gray-700 font-medium">{item.label}</span>
                            <span className={`text-sm font-bold ${item.color}`}>{item.value}</span>
                          </div>
                        ))}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                ) : (
                  /* Flat breakdown for other cards */
                  detailsData.breakdown && detailsData.breakdown.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Répartition
                      </h4>
                      <div className="space-y-2">
                        {detailsData.breakdown.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                          >
                            <span className="text-sm text-gray-700 font-medium">
                              {item.label}
                            </span>
                            <span className={`text-sm font-bold ${item.color}`}>
                              {item.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )}

                {/* Comparison Section */}
                {detailsData.comparison && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">
                        {detailsData.comparison.period}
                      </span>
                      <div className={`
                        flex items-center gap-1 px-2 py-1 rounded-full
                        ${detailsData.comparison.isPositive
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-rose-100 text-rose-700'
                        }
                      `}>
                        {detailsData.comparison.isPositive ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        <span className="text-xs font-bold">
                          {detailsData.comparison.change}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Last Updated */}
                {detailsData.lastUpdated && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-400 text-center">
                      {detailsData.lastUpdated}
                    </p>
                  </div>
                )}
              </div>

              {/* Popup Arrow pointing upward (since popup is always below card) */}
              <div
                className="absolute w-3 h-3 bg-white border transform rotate-45 -top-1.5 left-6 border-b-0 border-r-0 border-gray-200"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

StatsCard.displayName = 'StatsCard';

export default StatsCard;
